import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Copy,
  Download,
  Eraser,
  Eye,
  FileDown,
  FileImage,
  FileJson,
  FileText,
  FolderOpen,
  Grid3X3,
  ImageUp,
  PaintBucket,
  Pencil,
  Pipette,
  Redo2,
  RotateCcw,
  RotateCw,
  Save,
  Sparkles,
  Trash2,
  Undo2,
  WandSparkles,
  type LucideIcon
} from 'lucide-react';
import PatternCanvas from './components/PatternCanvas';
import TemplateGallery from './components/TemplateGallery';
import { genericMidiPalette, getPaletteColor, visiblePaletteColors } from './data/palette';
import { templates } from './data/templates';
import {
  countCells,
  createBlankDesign,
  generatePatternFromImage,
  loadImage,
  makeDesign,
  makeLocalRecommendation,
  replaceColor,
  totalBeads,
  usedColorCount
} from './lib/pattern';
import { clearDraft, deleteDesign, listDesigns, loadDesign, loadDraft, saveDesign, saveDraft } from './lib/draftStore';
import { exportCsv, exportJson, exportPdf, exportPng } from './lib/exporters';
import { readableTextColor } from './lib/color';
import {
  backgroundModeLabel,
  colorName,
  detectBrowserLocale,
  difficultyLabel,
  editorToolLabel,
  formatTime,
  localeOptions,
  patternModeLabel,
  setDocumentLocale,
  t,
  templateTitle,
  type Locale
} from './i18n';
import type {
  AiRecommendation,
  BeadDesign,
  CellColorId,
  EditorTool,
  ExportOptions,
  GridSize,
  MaxColors,
  PatternSettings,
  PatternMode,
  TemplatePattern
} from './types';

const palette = genericMidiPalette;
const initialLocale = detectBrowserLocale();
const editorTools: Array<[EditorTool, LucideIcon]> = [
  ['pencil', Pencil],
  ['eraser', Eraser],
  ['fill', PaintBucket],
  ['eyedropper', Pipette]
];

const defaultSettings: PatternSettings = {
  sourceType: 'template',
  mode: 'quick' as PatternMode,
  gridSize: 29 as GridSize,
  maxColors: 12 as MaxColors,
  paletteId: palette.id,
  backgroundMode: 'transparent',
  transparentAlphaThreshold: 16,
  rotation: 0,
  zoom: 1,
  dithering: false,
  cleanup: {
    removeIsolatedPixels: true,
    minRegionSize: 1,
    outlineEnhancement: false
  }
};

function designFromTemplate(template: TemplatePattern, locale: Locale): BeadDesign {
  return makeDesign({
    id: `des_${template.id}`,
    title: templateTitle(locale, template),
    width: template.width,
    height: template.height,
    paletteId: template.paletteId,
    cells: [...template.cells],
    settings: {
      ...defaultSettings,
      sourceType: 'template',
      gridSize: template.width as GridSize
    }
  });
}

function clampHistory(items: CellColorId[][]) {
  return items.slice(Math.max(0, items.length - 50));
}

function App() {
  const [locale, setLocale] = useState<Locale>(initialLocale);
  const [design, setDesign] = useState(() => designFromTemplate(templates[0], initialLocale));
  const [settings, setSettings] = useState(defaultSettings);
  const [selectedTool, setSelectedTool] = useState<EditorTool>('pencil');
  const [selectedColorId, setSelectedColorId] = useState<CellColorId>('orange');
  const [replaceFromId, setReplaceFromId] = useState<CellColorId>('orange');
  const [replaceToId, setReplaceToId] = useState<CellColorId>('coral');
  const [canvasZoom, setCanvasZoom] = useState(16);
  const [showGrid, setShowGrid] = useState(true);
  const [showSymbols, setShowSymbols] = useState(false);
  const [historyPast, setHistoryPast] = useState<CellColorId[][]>([]);
  const [historyFuture, setHistoryFuture] = useState<CellColorId[][]>([]);
  const [uploadedImage, setUploadedImage] = useState<HTMLImageElement | null>(null);
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [recommendation, setRecommendation] = useState<AiRecommendation | null>(null);
  const [activeCategory, setActiveCategory] = useState<TemplatePattern['category'] | 'all'>('all');
  const [savedDesigns, setSavedDesigns] = useState<BeadDesign[]>([]);
  const [status, setStatus] = useState(() => t(initialLocale, 'ready'));
  const [autosaveStatus, setAutosaveStatus] = useState(() => t(initialLocale, 'localDraftPending'));
  const [exportOptions, setExportOptions] = useState<ExportOptions>({
    includeGrid: true,
    includeSymbols: false,
    scale: 12,
    background: 'white'
  });
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const filteredTemplates = useMemo(
    () => templates.filter((template) => activeCategory === 'all' || template.category === activeCategory),
    [activeCategory]
  );

  const sortedCounts = useMemo(
    () => Object.entries(design.counts).sort((a, b) => b[1] - a[1]),
    [design.counts]
  );

  const refreshSavedDesigns = () => {
    listDesigns()
      .then(setSavedDesigns)
      .catch(() => setStatus(t(locale, 'unableReadLocalDesigns')));
  };

  useEffect(() => {
    setDocumentLocale(locale);
  }, [locale]);

  useEffect(() => {
    if (recommendation) {
      setRecommendation(makeLocalRecommendation(uploadedImage, settings, locale));
    }
  }, [locale]);

  useEffect(() => {
    loadDraft()
      .then((draft) => {
        if (draft) {
          setDesign(draft);
          setSettings(draft.settings);
          setAutosaveStatus(t(locale, 'recoveredLocalDraft'));
        }
      })
      .catch(() => setAutosaveStatus(t(locale, 'draftStorageUnavailable')));
    refreshSavedDesigns();
  }, []);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      saveDraft(design)
        .then(() => setAutosaveStatus(t(locale, 'savedLocallyAt', { time: formatTime(locale, new Date()) })))
        .catch(() => setAutosaveStatus(t(locale, 'localAutosaveFailed')));
    }, 700);

    return () => window.clearTimeout(timeout);
  }, [design, locale]);

  const commitCells = (cells: CellColorId[], label: string) => {
    setHistoryPast((past) => clampHistory([...past, design.cells]));
    setHistoryFuture([]);
    setDesign((current) => ({
      ...current,
      cells,
      counts: countCells(cells),
      updatedAt: new Date().toISOString()
    }));
    setStatus(label);
  };

  const undo = () => {
    setHistoryPast((past) => {
      if (!past.length) return past;
      const previous = past[past.length - 1];
      setHistoryFuture((future) => clampHistory([design.cells, ...future]));
      setDesign((current) => ({
        ...current,
        cells: previous,
        counts: countCells(previous),
        updatedAt: new Date().toISOString()
      }));
      setStatus(t(locale, 'undo'));
      return past.slice(0, -1);
    });
  };

  const redo = () => {
    setHistoryFuture((future) => {
      if (!future.length) return future;
      const next = future[0];
      setHistoryPast((past) => clampHistory([...past, design.cells]));
      setDesign((current) => ({
        ...current,
        cells: next,
        counts: countCells(next),
        updatedAt: new Date().toISOString()
      }));
      setStatus(t(locale, 'redo'));
      return future.slice(1);
    });
  };

  const handleFile = async (file: File) => {
    setUploadError(null);
    if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) {
      setUploadError(t(locale, 'unsupportedFileType'));
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      setUploadError(t(locale, 'fileTooLarge'));
      return;
    }

    try {
      const image = await loadImage(file);
      setUploadedImage(image);
      setUploadedFileName(file.name);
      setRecommendation(makeLocalRecommendation(image, settings, locale));
      setStatus(t(locale, 'imageLoaded'));
    } catch {
      setUploadError(t(locale, 'imageDecodeFailed'));
    }
  };

  const generateFromUpload = () => {
    if (!uploadedImage) {
      setStatus(t(locale, 'chooseImageFirst'));
      return;
    }
    try {
      const uploadSettings = { ...settings, sourceType: 'upload' as const };
      const cells = generatePatternFromImage(uploadedImage, palette, uploadSettings);
      setHistoryPast([]);
      setHistoryFuture([]);
      setDesign(
        makeDesign({
          title: uploadedFileName ? uploadedFileName.replace(/\.[^.]+$/, '') : t(locale, 'uploadedPattern'),
          width: uploadSettings.gridSize,
          height: uploadSettings.gridSize,
          paletteId: palette.id,
          cells,
          settings: uploadSettings,
          assets: {
            originalImageName: uploadedFileName ?? undefined,
            originalImageUrl: uploadedImage.src
          }
        })
      );
      setStatus(t(locale, 'patternGenerated'));
    } catch {
      setStatus(t(locale, 'generationFailed'));
    }
  };

  const selectTemplate = (template: TemplatePattern) => {
    const next = designFromTemplate(template, locale);
    setDesign(next);
    setSettings(next.settings);
    setHistoryPast([]);
    setHistoryFuture([]);
    setRecommendation(null);
    setStatus(t(locale, 'templateLoaded'));
  };

  const applyRecommendation = () => {
    if (!recommendation) return;
    setSettings((current) => ({
      ...current,
      gridSize: recommendation.recommended_grid.width,
      maxColors: recommendation.recommended_color_count.count,
      backgroundMode: recommendation.background_recommendation
    }));
    setStatus(t(locale, 'recommendationApplied'));
  };

  const replaceSelectedColor = () => {
    const next = replaceColor(design.cells, replaceFromId, replaceToId);
    commitCells(next, t(locale, 'colorReplaced'));
  };

  const saveCurrentDesign = async () => {
    await saveDesign({ ...design, updatedAt: new Date().toISOString() });
    await clearDraft();
    refreshSavedDesigns();
    setStatus(t(locale, 'designSaved'));
  };

  const openSavedDesign = async (id: string) => {
    const saved = await loadDesign(id);
    if (!saved) return;
    setDesign(saved);
    setSettings(saved.settings);
    setHistoryPast([]);
    setHistoryFuture([]);
    setStatus(t(locale, 'designLoaded'));
  };

  const duplicateSavedDesign = async (saved: BeadDesign) => {
    await saveDesign(
      makeDesign({
        title: t(locale, 'copyTitle', { title: saved.title }),
        width: saved.width,
        height: saved.height,
        paletteId: saved.paletteId,
        cells: [...saved.cells],
        settings: saved.settings,
        assets: saved.assets
      })
    );
    refreshSavedDesigns();
    setStatus(t(locale, 'designDuplicated'));
  };

  const deleteSavedDesign = async (id: string) => {
    await deleteDesign(id);
    refreshSavedDesigns();
    setStatus(t(locale, 'designDeleted'));
  };

  const blankCanvas = () => {
    const blank = createBlankDesign(settings, palette, t(locale, 'untitledBeadPattern'));
    setDesign(blank);
    setHistoryPast([]);
    setHistoryFuture([]);
    setStatus(t(locale, 'blankGridCreated'));
  };

  return (
    <div className="app-shell">
      <header className="topbar">
        <div className="brand">
          <span className="brand-mark" aria-hidden="true">
            <span />
            <span />
            <span />
            <span />
          </span>
          <div>
            <strong>Bead Studio AI</strong>
            <small>{t(locale, 'patternDesigner')}</small>
          </div>
        </div>

        <nav className="topbar-actions" aria-label={t(locale, 'primaryActions')}>
          <label className="locale-control">
            <span className="visually-hidden">{t(locale, 'language')}</span>
            <select
              value={locale}
              onChange={(event) => setLocale(event.target.value as Locale)}
              aria-label={t(locale, 'language')}
            >
              {localeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <button type="button" onClick={() => fileInputRef.current?.click()}>
            <ImageUp size={17} />
            {t(locale, 'uploadImage')}
          </button>
          <button type="button" onClick={() => document.querySelector('#templates')?.scrollIntoView({ behavior: 'smooth' })}>
            <FolderOpen size={17} />
            {t(locale, 'browseTemplates')}
          </button>
          <button type="button" className="primary" onClick={generateFromUpload}>
            <WandSparkles size={17} />
            {t(locale, 'tryQuickMode')}
          </button>
          <button type="button" onClick={saveCurrentDesign}>
            <Save size={17} />
            {t(locale, 'save')}
          </button>
          <button type="button" onClick={() => exportPng(design, palette, exportOptions)}>
            <Download size={17} />
            {t(locale, 'exportPng')}
          </button>
        </nav>

        <input
          ref={fileInputRef}
          className="visually-hidden"
          type="file"
          accept="image/png,image/jpeg,image/webp"
          onChange={(event) => {
            const file = event.currentTarget.files?.[0];
            if (file) void handleFile(file);
            event.currentTarget.value = '';
          }}
        />
      </header>

      <main className="workspace">
        <aside className="left-panel panel-scroll">
          <section
            className="panel-section upload-zone"
            onDragOver={(event) => event.preventDefault()}
            onDrop={(event) => {
              event.preventDefault();
              const file = event.dataTransfer.files[0];
              if (file) void handleFile(file);
            }}
          >
            <div className="section-heading-row">
              <div>
                <h2>{t(locale, 'uploadImage')}</h2>
                <p>{uploadedFileName ?? t(locale, 'uploadFallback')}</p>
              </div>
              <button className="icon-button" type="button" onClick={() => fileInputRef.current?.click()} aria-label={t(locale, 'chooseImage')}>
                <ImageUp size={18} />
              </button>
            </div>
            <button className="drop-target" type="button" onClick={() => fileInputRef.current?.click()}>
              {uploadedImage ? (
                <img src={uploadedImage.src} alt={t(locale, 'uploadedSourcePreview')} />
              ) : (
                <>
                  <ImageUp size={28} />
                  <span>{t(locale, 'dropImage')}</span>
                </>
              )}
            </button>
            {uploadError ? <p className="field-error">{uploadError}</p> : null}
          </section>

          <section className="panel-section">
            <div className="section-heading-row">
              <div>
                <h2>{t(locale, 'setup')}</h2>
                <p>{patternModeLabel(locale, settings.mode)} · {settings.gridSize}x{settings.gridSize}</p>
              </div>
              <button className="icon-button" type="button" onClick={blankCanvas} aria-label={t(locale, 'newBlankGrid')}>
                <Grid3X3 size={18} />
              </button>
            </div>

            <label className="field">
              <span>{t(locale, 'gridSize')}</span>
              <select
                value={settings.gridSize}
                onChange={(event) =>
                  setSettings((current) => ({ ...current, gridSize: Number(event.target.value) as GridSize }))
                }
              >
                {[29, 58, 80, 100].map((size) => (
                  <option key={size} value={size}>
                    {size} x {size}
                  </option>
                ))}
              </select>
            </label>

            <label className="field">
              <span>{t(locale, 'maximumColors')}</span>
              <select
                value={settings.maxColors}
                onChange={(event) =>
                  setSettings((current) => ({ ...current, maxColors: Number(event.target.value) as MaxColors }))
                }
              >
                {[8, 12, 16, 24].map((count) => (
                  <option key={count} value={count}>
                    {count}
                  </option>
                ))}
              </select>
            </label>

            <div className="segmented-control" role="group" aria-label={t(locale, 'generationMode')}>
              {(['quick', 'ai_assist', 'ai_stylize'] as PatternMode[]).map((mode) => (
                <button
                  key={mode}
                  type="button"
                  className={settings.mode === mode ? 'active' : ''}
                  onClick={() => setSettings((current) => ({ ...current, mode }))}
                >
                  {patternModeLabel(locale, mode)}
                </button>
              ))}
            </div>

            <label className="field">
              <span>{t(locale, 'background')}</span>
              <select
                value={settings.backgroundMode}
                onChange={(event) =>
                  setSettings((current) => ({ ...current, backgroundMode: event.target.value as typeof settings.backgroundMode }))
                }
              >
                <option value="transparent">{backgroundModeLabel(locale, 'transparent')}</option>
                <option value="keep">{backgroundModeLabel(locale, 'keep')}</option>
                <option value="solid">{backgroundModeLabel(locale, 'solid')}</option>
                <option value="simplify">{backgroundModeLabel(locale, 'simplify')}</option>
              </select>
            </label>

            <div className="setting-row">
              <span>{t(locale, 'rotate')}</span>
              <div className="button-pair">
                <button
                  type="button"
                  onClick={() => setSettings((current) => ({ ...current, rotation: current.rotation - 90 }))}
                  aria-label={t(locale, 'rotateLeft')}
                >
                  <RotateCcw size={16} />
                </button>
                <button
                  type="button"
                  onClick={() => setSettings((current) => ({ ...current, rotation: current.rotation + 90 }))}
                  aria-label={t(locale, 'rotateRight')}
                >
                  <RotateCw size={16} />
                </button>
              </div>
            </div>

            <label className="field">
              <span>{t(locale, 'cropZoom')}</span>
              <input
                type="range"
                min="0.8"
                max="2"
                step="0.05"
                value={settings.zoom}
                onChange={(event) => setSettings((current) => ({ ...current, zoom: Number(event.target.value) }))}
              />
            </label>

            <label className="toggle-row">
              <input
                type="checkbox"
                checked={settings.cleanup.removeIsolatedPixels}
                onChange={(event) =>
                  setSettings((current) => ({
                    ...current,
                    cleanup: { ...current.cleanup, removeIsolatedPixels: event.target.checked }
                  }))
                }
              />
              <span>{t(locale, 'cleanupIsolatedCells')}</span>
            </label>
          </section>

          <section className="panel-section ai-panel">
            <div className="section-heading-row">
              <div>
                <h2>{t(locale, 'aiAssist')}</h2>
                <p>{recommendation ? difficultyLabel(locale, recommendation.difficulty) : t(locale, 'structuredRecommendation')}</p>
              </div>
              <button
                className="icon-button"
                type="button"
                onClick={() => setRecommendation(makeLocalRecommendation(uploadedImage, settings, locale))}
                aria-label={t(locale, 'runLocalAiAssist')}
              >
                <Sparkles size={18} />
              </button>
            </div>

            {recommendation ? (
              <div className="recommendation-box">
                <strong>{recommendation.subject}</strong>
                <span>
                  {t(locale, 'recommendationMeta', {
                    width: recommendation.recommended_grid.width,
                    height: recommendation.recommended_grid.height,
                    colors: recommendation.recommended_color_count.count
                  })}
                </span>
                <p>{recommendation.recommended_grid.reason}</p>
                {recommendation.warnings.map((warning) => (
                  <em key={warning}>{warning}</em>
                ))}
                <button type="button" className="primary full-width" onClick={applyRecommendation}>
                  {t(locale, 'useTheseSettings')}
                </button>
              </div>
            ) : (
              <div className="empty-state">{t(locale, 'noRecommendationYet')}</div>
            )}
          </section>

          <div id="templates">
            <TemplateGallery
              templates={filteredTemplates}
              palette={palette}
              locale={locale}
              activeCategory={activeCategory}
              onCategoryChange={setActiveCategory}
              onSelectTemplate={selectTemplate}
            />
          </div>
        </aside>

        <section className="canvas-stage" aria-label={t(locale, 'patternEditor')}>
          <div className="stage-toolbar">
            <div>
              <h1>{design.title}</h1>
              <p>
                {t(locale, 'patternMeta', {
                  width: design.width,
                  height: design.height,
                  colors: usedColorCount(design.counts),
                  beads: totalBeads(design.counts)
                })}
              </p>
            </div>

            <div className="stage-actions">
              <button type="button" onClick={undo} disabled={!historyPast.length} aria-label={t(locale, 'undo')}>
                <Undo2 size={17} />
              </button>
              <button type="button" onClick={redo} disabled={!historyFuture.length} aria-label={t(locale, 'redo')}>
                <Redo2 size={17} />
              </button>
              <label className="zoom-control">
                <span>{t(locale, 'zoom')}</span>
                <input
                  type="range"
                  min="7"
                  max="26"
                  value={canvasZoom}
                  onChange={(event) => setCanvasZoom(Number(event.target.value))}
                />
              </label>
              <button type="button" className={showGrid ? 'active-icon' : ''} onClick={() => setShowGrid((value) => !value)} aria-label={t(locale, 'toggleGrid')}>
                <Grid3X3 size={17} />
              </button>
              <button
                type="button"
                className={showSymbols ? 'active-icon' : ''}
                onClick={() => setShowSymbols((value) => !value)}
                aria-label={t(locale, 'toggleSymbols')}
              >
                <Eye size={17} />
              </button>
            </div>
          </div>

          <div className="canvas-scroll">
            <PatternCanvas
              design={design}
              palette={palette}
              selectedColorId={selectedColorId}
              selectedTool={selectedTool}
              zoom={canvasZoom}
              showGrid={showGrid}
              showSymbols={showSymbols}
              onPickColor={setSelectedColorId}
              onCommitCells={commitCells}
              labels={{
                editor: t(locale, 'beadGridEditor', { title: design.title }),
                fillRegion: t(locale, 'fillRegion'),
                eraseCells: t(locale, 'eraseCells'),
                paintCells: t(locale, 'paintCells')
              }}
            />
          </div>

          <footer className="statusbar">
            <span>{status}</span>
            <span>{autosaveStatus}</span>
            <span>{t(locale, 'canvasCellSize', { size: canvasZoom })}</span>
          </footer>
        </section>

        <aside className="right-panel panel-scroll">
          <section className="panel-section">
            <div className="section-heading-row">
              <div>
                <h2>{t(locale, 'tools')}</h2>
                <p>{editorToolLabel(locale, selectedTool)}</p>
              </div>
            </div>
            <div className="tool-grid">
              {editorTools.map(([tool, Icon]) => (
                <button
                  key={tool}
                  type="button"
                  className={selectedTool === tool ? 'active' : ''}
                  onClick={() => setSelectedTool(tool)}
                  title={editorToolLabel(locale, tool)}
                >
                  <Icon size={18} />
                  <span>{editorToolLabel(locale, tool)}</span>
                </button>
              ))}
            </div>
          </section>

          <section className="panel-section">
            <div className="section-heading-row">
              <div>
                <h2>{t(locale, 'palette')}</h2>
                <p>{t(locale, 'colorCount', { count: palette.colors.length - 1 })}</p>
              </div>
              <span
                className="selected-swatch"
                style={{
                  background: getPaletteColor(palette, selectedColorId).hex,
                  color: readableTextColor(getPaletteColor(palette, selectedColorId).hex)
                }}
              >
                {getPaletteColor(palette, selectedColorId).symbol}
              </span>
            </div>
            <div className="palette-grid">
              {visiblePaletteColors(palette).map((color) => (
                <button
                  key={color.id}
                  type="button"
                  className={selectedColorId === color.id ? 'selected' : ''}
                  title={t(locale, 'selectedColorTitle', { name: colorName(locale, color), id: color.id })}
                  onClick={() => setSelectedColorId(color.id)}
                  style={{ background: color.hex, color: readableTextColor(color.hex) }}
                >
                  {color.symbol}
                </button>
              ))}
            </div>
          </section>

          <section className="panel-section">
            <div className="section-heading-row">
              <div>
                <h2>{t(locale, 'replaceColor')}</h2>
                <p>{t(locale, 'globalSwap')}</p>
              </div>
            </div>
            <div className="replace-grid">
              <select value={replaceFromId} onChange={(event) => setReplaceFromId(event.target.value)}>
                {visiblePaletteColors(palette).map((color) => (
                  <option key={color.id} value={color.id}>
                    {colorName(locale, color)}
                  </option>
                ))}
              </select>
              <select value={replaceToId} onChange={(event) => setReplaceToId(event.target.value)}>
                {visiblePaletteColors(palette).map((color) => (
                  <option key={color.id} value={color.id}>
                    {colorName(locale, color)}
                  </option>
                ))}
              </select>
              <button type="button" onClick={replaceSelectedColor}>
                <Copy size={16} />
                {t(locale, 'replace')}
              </button>
            </div>
          </section>

          <section className="panel-section">
            <div className="section-heading-row">
              <div>
                <h2>{t(locale, 'beadCounts')}</h2>
                <p>{t(locale, 'totalBeads', { count: totalBeads(design.counts) })}</p>
              </div>
            </div>
            <div className="count-list">
              {sortedCounts.length ? (
                sortedCounts.map(([colorId, quantity]) => {
                  const color = getPaletteColor(palette, colorId);
                  return (
                    <div className="count-row" key={colorId}>
                      <span className="count-swatch" style={{ background: color.hex }} />
                      <span>{colorName(locale, color)}</span>
                      <code>{color.symbol}</code>
                      <strong>{quantity}</strong>
                    </div>
                  );
                })
              ) : (
                <div className="empty-state">{t(locale, 'noBeadsYet')}</div>
              )}
            </div>
          </section>

          <section className="panel-section">
            <div className="section-heading-row">
              <div>
                <h2>{t(locale, 'export')}</h2>
                <p>{t(locale, 'exportTypes')}</p>
              </div>
            </div>
            <div className="export-options">
              <label className="toggle-row">
                <input
                  type="checkbox"
                  checked={exportOptions.includeGrid}
                  onChange={(event) => setExportOptions((current) => ({ ...current, includeGrid: event.target.checked }))}
                />
                <span>{t(locale, 'gridLines')}</span>
              </label>
              <label className="toggle-row">
                <input
                  type="checkbox"
                  checked={exportOptions.includeSymbols}
                  onChange={(event) => setExportOptions((current) => ({ ...current, includeSymbols: event.target.checked }))}
                />
                <span>{t(locale, 'symbols')}</span>
              </label>
            </div>
            <div className="export-grid">
              <button type="button" onClick={() => exportPng(design, palette, exportOptions)}>
                <FileImage size={16} />
                PNG
              </button>
              <button type="button" onClick={() => exportJson(design, palette)}>
                <FileJson size={16} />
                JSON
              </button>
              <button type="button" onClick={() => exportCsv(design, palette, locale)}>
                <FileText size={16} />
                CSV
              </button>
              <button type="button" onClick={() => exportPdf(design, palette, exportOptions, locale)}>
                <FileDown size={16} />
                PDF
              </button>
            </div>
          </section>

          <section className="panel-section">
            <div className="section-heading-row">
              <div>
                <h2>{t(locale, 'myDesigns')}</h2>
                <p>{t(locale, 'savedLocallyCount', { count: savedDesigns.length })}</p>
              </div>
              <button className="icon-button" type="button" onClick={saveCurrentDesign} aria-label={t(locale, 'saveCurrentDesign')}>
                <Save size={18} />
              </button>
            </div>
            <div className="saved-list">
              {savedDesigns.length ? (
                savedDesigns.map((saved) => (
                  <div className="saved-row" key={saved.id}>
                    <button type="button" onClick={() => void openSavedDesign(saved.id)}>
                      <span>{saved.title}</span>
                      <small>
                        {t(locale, 'savedDesignMeta', {
                          width: saved.width,
                          height: saved.height,
                          beads: totalBeads(saved.counts)
                        })}
                      </small>
                    </button>
                    <button type="button" onClick={() => void duplicateSavedDesign(saved)} aria-label={t(locale, 'duplicateDesign', { title: saved.title })}>
                      <Copy size={15} />
                    </button>
                    <button type="button" onClick={() => void deleteSavedDesign(saved.id)} aria-label={t(locale, 'deleteDesign', { title: saved.title })}>
                      <Trash2 size={15} />
                    </button>
                  </div>
                ))
              ) : (
                <div className="empty-state">{t(locale, 'noSavedDesigns')}</div>
              )}
            </div>
          </section>
        </aside>
      </main>
    </div>
  );
}

export default App;
